export const ADS_MEASUREMENT_COOKIE = 'monery_ads_measurement';
export const ADS_MEASUREMENT_CONSENT_VERSION = 2 as const;
export const ADS_MEASUREMENT_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const IDENTIFIER_NAMES = ['gclid', 'gbraid', 'wbraid', 'fbc', 'fbp'] as const;
const IDENTIFIER_PATTERN = /^[A-Za-z0-9._~-]+$/;

type AdsIdentifierName = (typeof IDENTIFIER_NAMES)[number];

export type AdsMeasurementContext = {
    advertisingConsent: true;
    capturedAt: string;
    clientUserAgent?: string;
    consentVersion: typeof ADS_MEASUREMENT_CONSENT_VERSION;
} & Partial<Record<AdsIdentifierName, string>>;

export type UpdateAdsMeasurementInput = Partial<Record<AdsIdentifierName, string>> & {
    advertisingConsent: boolean;
    capturedAt?: string;
    clientUserAgent?: string;
    consentVersion: typeof ADS_MEASUREMENT_CONSENT_VERSION;
    expectedGeneration: number;
};

export type AdsConsentState = {
    advertisingConsent: boolean;
    generation: number;
};

export const ADS_CONSENT_STATE_STORAGE_PREFIX = 'monery_ads_consent_state:';
const fallbackAdsConsentStates = new Map<string, AdsConsentState>();

type UpdateAdsMeasurement = (input: UpdateAdsMeasurementInput) => Promise<AdsConsentState>;

export const isAdsMeasurementEnabled = (value: string | undefined): boolean => value === 'true';

const isAdsConsentState = (value: unknown): value is AdsConsentState => {
    if (!value || typeof value !== 'object') return false;
    const state = value as Record<string, unknown>;
    return (
        typeof state.advertisingConsent === 'boolean' &&
        Number.isInteger(state.generation) &&
        Number(state.generation) >= 0
    );
};

export const readStoredAdsConsentState = (userId: string): AdsConsentState | null => {
    if (typeof window === 'undefined') return null;
    const fallback = fallbackAdsConsentStates.get(userId) ?? null;
    try {
        const value = window.localStorage.getItem(`${ADS_CONSENT_STATE_STORAGE_PREFIX}${userId}`);
        if (!value) return fallback;
        const state = JSON.parse(value) as unknown;
        if (!isAdsConsentState(state)) return fallback;
        const stored = { advertisingConsent: state.advertisingConsent, generation: state.generation };
        return fallback && fallback.generation > stored.generation ? fallback : stored;
    } catch (error) {
        return fallback;
    }
};

export const writeStoredAdsConsentState = (userId: string, state: AdsConsentState): void => {
    if (typeof window === 'undefined' || !isAdsConsentState(state)) return;
    const current = readStoredAdsConsentState(userId);
    if (current && current.generation > state.generation) return;
    const safeState = { advertisingConsent: state.advertisingConsent, generation: state.generation };
    fallbackAdsConsentStates.set(userId, safeState);
    try {
        window.localStorage.setItem(`${ADS_CONSENT_STATE_STORAGE_PREFIX}${userId}`, JSON.stringify(safeState));
    } catch (error) {
        // The in-memory state prevents duplicate syncs while browser storage is unavailable.
    }
};

export const shouldSkipExternalConsentSync = (
    source: 'initial' | 'local' | 'external',
    userId: string,
    previousUserId: string | undefined,
): boolean => source === 'external' && previousUserId === userId;

export const shouldSyncAdsMeasurementConsent = (measurementEnabled: boolean, advertisingConsent: boolean): boolean =>
    measurementEnabled || !advertisingConsent;

const isValidIdentifier = (value: unknown): value is string =>
    typeof value === 'string' && value.length > 0 && value.length <= 200 && IDENTIFIER_PATTERN.test(value);

const isValidClientUserAgent = (value: unknown): value is string =>
    typeof value === 'string' && value.length > 0 && value.length <= 512;

const getCookieDomain = (): string => {
    const domain = process.env.NEXT_PUBLIC_API_COOKIE_DOMAIN;
    return domain && domain !== 'localhost' ? `; Domain=${domain}` : '';
};

const eraseCookie = (name: string) => {
    const expired = `${name}=; path=/; max-age=0; SameSite=Lax`;
    document.cookie = expired;
    const domain = getCookieDomain();
    if (domain) document.cookie = `${expired}${domain}`;
};

const getCookieValue = (name: string): string | null => {
    const cookie = document.cookie
        .split(';')
        .map((value) => value.trim())
        .find((value) => value.startsWith(`${name}=`));
    return cookie ? cookie.slice(name.length + 1) : null;
};

const parseContext = (value: string | null, now: Date): AdsMeasurementContext | null => {
    if (!value) return null;

    try {
        const parsed = JSON.parse(decodeURIComponent(value)) as Record<string, unknown>;
        const capturedAt = typeof parsed.capturedAt === 'string' ? new Date(parsed.capturedAt) : null;
        if (
            parsed.advertisingConsent !== true ||
            parsed.consentVersion !== ADS_MEASUREMENT_CONSENT_VERSION ||
            !capturedAt ||
            Number.isNaN(capturedAt.getTime()) ||
            now.getTime() - capturedAt.getTime() > ADS_MEASUREMENT_MAX_AGE_MS ||
            capturedAt.getTime() > now.getTime()
        ) {
            return null;
        }

        const identifiers = Object.fromEntries(
            IDENTIFIER_NAMES.flatMap((name) => (isValidIdentifier(parsed[name]) ? [[name, parsed[name]]] : [])),
        );
        const clientUserAgent = isValidClientUserAgent(parsed.clientUserAgent) ? parsed.clientUserAgent : undefined;

        return {
            advertisingConsent: true,
            capturedAt: capturedAt.toISOString(),
            clientUserAgent,
            consentVersion: ADS_MEASUREMENT_CONSENT_VERSION,
            ...identifiers,
        };
    } catch (error) {
        return null;
    }
};

export const readAdsMeasurementContext = (now = new Date()): AdsMeasurementContext | null => {
    if (typeof document === 'undefined') return null;
    const value = getCookieValue(ADS_MEASUREMENT_COOKIE);
    const context = parseContext(value, now);
    if (value && !context) eraseCookie(ADS_MEASUREMENT_COOKIE);
    return context;
};

const getQueryIdentifiers = (): Partial<Record<AdsIdentifierName, string>> => {
    const query = new URLSearchParams(window.location.search);
    const identifiers: Partial<Record<AdsIdentifierName, string>> = {};
    IDENTIFIER_NAMES.forEach((name) => {
        const queryValue = query.get(name);
        if (isValidIdentifier(queryValue)) identifiers[name] = queryValue;
    });
    return identifiers;
};

const getFbclid = (): string | null => {
    const value = new URLSearchParams(window.location.search).get('fbclid');
    return isValidIdentifier(value) ? value : null;
};

const fbcContainsFbclid = (fbc: string | undefined, fbclid: string): boolean => {
    if (!fbc || !isValidIdentifier(fbc)) return false;
    const match = /^fb\.1\.\d+\.(.+)$/.exec(fbc);
    return match?.[1] === fbclid;
};

const getFbcFromFbclid = (
    fbclid: string | null,
    now: Date,
    existingFbc?: string,
    vendorFbc?: string,
): string | null => {
    if (!fbclid) return null;
    if (fbcContainsFbclid(existingFbc, fbclid)) return existingFbc || null;
    if (fbcContainsFbclid(vendorFbc, fbclid)) return vendorFbc || null;

    const fbc = `fb.1.${now.getTime()}.${fbclid}`;
    return isValidIdentifier(fbc) ? fbc : null;
};

const getVendorIdentifiers = (): Partial<Record<AdsIdentifierName, string>> => {
    const identifiers: Partial<Record<AdsIdentifierName, string>> = {};
    (['fbc', 'fbp'] as const).forEach((name) => {
        const value = getCookieValue(`_${name}`);
        if (isValidIdentifier(value)) identifiers[name] = value;
    });
    return identifiers;
};

const writeContext = (context: AdsMeasurementContext) => {
    const secure = process.env.NODE_ENV === 'production' ? '; Secure' : '';
    const maxAge = Math.floor(ADS_MEASUREMENT_MAX_AGE_MS / 1000);
    document.cookie = `${ADS_MEASUREMENT_COOKIE}=${encodeURIComponent(
        JSON.stringify(context),
    )}; path=/; max-age=${maxAge}; SameSite=Lax${secure}${getCookieDomain()}`;
};

export const captureAdsMeasurementContextResult = (
    advertisingConsent: boolean,
    now = new Date(),
): { changed: boolean; context: AdsMeasurementContext | null; explicitClick: boolean } => {
    if (typeof window === 'undefined' || !advertisingConsent) {
        return { changed: false, context: null, explicitClick: false };
    }

    const existing = readAdsMeasurementContext(now);
    const queryIdentifiers = getQueryIdentifiers();
    const vendorIdentifiers = getVendorIdentifiers();
    const fbclid = getFbclid();
    const fbcFromFbclid = getFbcFromFbclid(fbclid, now, existing?.fbc, vendorIdentifiers.fbc);
    const resolvedVendorIdentifiers = {
        ...vendorIdentifiers,
        ...(fbcFromFbclid ? { fbc: fbcFromFbclid } : {}),
    };
    const explicitClick =
        Object.entries(queryIdentifiers).some(([name, value]) => existing?.[name as AdsIdentifierName] !== value) ||
        Boolean(fbclid && fbcFromFbclid && !fbcContainsFbclid(existing?.fbc, fbclid));
    const vendorChanged = Object.entries(resolvedVendorIdentifiers).some(
        ([name, value]) => existing?.[name as AdsIdentifierName] !== value,
    );
    if (existing && !explicitClick && !vendorChanged) {
        return { changed: false, context: existing, explicitClick: false };
    }

    if (existing && !explicitClick && vendorChanged) {
        const context = {
            ...existing,
            ...resolvedVendorIdentifiers,
        };
        writeContext(context);
        return { changed: true, context, explicitClick: false };
    }

    const currentIdentifiers: Partial<Record<AdsIdentifierName, string>> = {
        ...queryIdentifiers,
        ...resolvedVendorIdentifiers,
    };
    if (explicitClick && !queryIdentifiers.fbc && !fbcFromFbclid) {
        delete currentIdentifiers.fbc;
    }
    if (!existing && Object.keys(currentIdentifiers).length === 0) {
        return { changed: false, context: null, explicitClick: false };
    }

    const context: AdsMeasurementContext = {
        advertisingConsent: true,
        capturedAt: now.toISOString(),
        clientUserAgent: isValidClientUserAgent(window.navigator.userAgent) ? window.navigator.userAgent : undefined,
        consentVersion: ADS_MEASUREMENT_CONSENT_VERSION,
        ...currentIdentifiers,
    };
    writeContext(context);
    return { changed: true, context, explicitClick };
};

export const captureAdsMeasurementContext = (
    advertisingConsent: boolean,
    now = new Date(),
): AdsMeasurementContext | null => captureAdsMeasurementContextResult(advertisingConsent, now).context;

export const pollLateAdsMeasurementContext = async ({
    capture,
    isActive,
    wait,
}: {
    capture: () => void;
    isActive: () => boolean;
    wait: (milliseconds: number) => Promise<void>;
}): Promise<void> => {
    const poll = async (attempt: number): Promise<void> => {
        await wait(500);
        if (!isActive()) return;
        capture();
        if (attempt < 3) await poll(attempt + 1);
    };
    await poll(0);
};

export const revokeAdsMeasurementContext = () => {
    if (typeof document === 'undefined') return;
    document.cookie
        .split(';')
        .map((cookie) => cookie.split('=')[0].trim())
        .filter(
            (name) => name === ADS_MEASUREMENT_COOKIE || name === '_fbc' || name === '_fbp' || name.startsWith('_gcl_'),
        )
        .forEach(eraseCookie);
};

export const syncAdsMeasurementContext = (
    update: UpdateAdsMeasurement,
    context: AdsMeasurementContext | null,
    expectedGeneration: number,
    advertisingConsent = Boolean(context?.advertisingConsent),
): Promise<AdsConsentState> =>
    update({
        ...(context || {}),
        advertisingConsent,
        consentVersion: ADS_MEASUREMENT_CONSENT_VERSION,
        expectedGeneration,
    });

export const settleAdsMeasurementSync = async ({
    isActive,
    onStale,
    onSuccess,
    request,
}: {
    isActive: () => boolean;
    onStale: (state: AdsConsentState) => void;
    onSuccess: (state: AdsConsentState) => void;
    request: () => Promise<AdsConsentState>;
}): Promise<void> => {
    try {
        const state = await request();
        if (isActive()) onSuccess(state);
    } catch (error) {
        const staleState = (error as Error & { adsConsentState?: AdsConsentState }).adsConsentState;
        if (isActive() && staleState) onStale(staleState);
    }
};
